import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { FileEntity } from './file.entity';
import { UserEntity } from './user.entity';

/**
 * Commercial driver's license class (CDL). Distinct from VehicleClass (A/B):
 * a driver's license can be A, B, or C.
 */
export enum DriverLicenseClass {
	CLASS_A = 'A',
	CLASS_B = 'B',
	CLASS_C = 'C',
}

/**
 * Driver-specific profile data, 1:1 with a `user` (PD-13761).
 *
 * A "driver" is a UserEntity carrying the RBAC role "Driver" — drivers log in,
 * do pre/post-trip inspections, and are referenced as `trip.fk_user__id_driver`.
 * The IDENTITY stays on `user`; this table holds only the structured, driver-
 * specific attributes (CDL + DOT medical) that would otherwise be null on every
 * non-driver user. Same extraction philosophy as the vehicle entity (PD-13767):
 * keep role-specific data out of the overloaded generic table.
 *
 * License / medical-card IMAGES are NOT stored here — they go in the shared
 * tenant files table (`_file`) and are referenced by the fk_file__id_* columns,
 * so documents stay in the one generic document store.
 *
 * One row per driver, created lazily on first save (no eager backfill).
 */
@Index('driver_profile__fk_user__id_uq', ['userId'], { unique: true })
@Entity('driver_profile')
export class DriverProfileEntity extends BaseEntity {
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public userId!: number;

	@Column('varchar', { name: 'licenseNumber', nullable: true, length: 64 })
	public licenseNumber!: string | null;

	/** Issuing state, 2-char (e.g. "MI"). */
	@Column('varchar', { name: 'licenseState', nullable: true, length: 2 })
	public licenseState!: string | null;

	@Column('enum', {
		name: 'licenseClass',
		enum: DriverLicenseClass,
		nullable: true,
	})
	public licenseClass!: DriverLicenseClass | null;

	/** Calendar dates (no time component) — stored as DATE, read as 'YYYY-MM-DD'. */
	@Column('date', { name: 'licenseExpiry', nullable: true })
	public licenseExpiry!: string | null;

	/** DOT medical examiner's certificate expiry — the compliance artifact. */
	@Column('date', { name: 'medicalCardExpiry', nullable: true })
	public medicalCardExpiry!: string | null;

	@Column('int', {
		name: 'fk_file__id_license_front',
		unsigned: true,
		nullable: true,
	})
	public licenseFrontFileId!: number | null;

	@Column('int', {
		name: 'fk_file__id_license_back',
		unsigned: true,
		nullable: true,
	})
	public licenseBackFileId!: number | null;

	@Column('int', {
		name: 'fk_file__id_medical_card',
		unsigned: true,
		nullable: true,
	})
	public medicalCardFileId!: number | null;

	@ManyToOne(() => UserEntity)
	@JoinColumn({ name: 'fk_user__id' })
	public fkUser!: UserEntity;

	@ManyToOne(() => FileEntity)
	@JoinColumn({ name: 'fk_file__id_license_front' })
	public fkLicenseFrontFile!: FileEntity | null;

	@ManyToOne(() => FileEntity)
	@JoinColumn({ name: 'fk_file__id_license_back' })
	public fkLicenseBackFile!: FileEntity | null;

	@ManyToOne(() => FileEntity)
	@JoinColumn({ name: 'fk_file__id_medical_card' })
	public fkMedicalCardFile!: FileEntity | null;
}
